import {
	FilterItem,
	FilterItemPascal,
	KeyedArray,
	keyPair,
	mapToKeyedArray,
	mapToKeyedArrayPascal,
} from "@repo/models";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

export const BRANCHES = "branches";
export const CHANNELS = "channels";
export const BRANDS = "brands";
export const MARKETERS = "marketers";
export const STATES = "states";

export const GROUP = {
	REP: "Rep",
	STATUS_CHANGE: "StatusChange",
};

export const REP = {
	REP_DETAILS: [GROUP.REP, GROUP.STATUS_CHANGE, "RepDetails"],
	REP_STATUS_HISTORIES: [GROUP.REP, GROUP.STATUS_CHANGE, "RepStatusHistories"],
	REP_ENROLLMENTS: [GROUP.REP, "RepEnrollments"],
	REP_STATUS_BUTTONS: [GROUP.REP, GROUP.STATUS_CHANGE, "StatusButtons"],
	REP_RELATED_REPS: [GROUP.REP, "RelatedReps"],
	REP_POT_DUPLICATES: [GROUP.REP, "DuplicateReps"],
	REP_UPDATE_DATA: [GROUP.REP, "UpdateData"],
	REP_ATTRIBUTES: [GROUP.REP, "RepAttributes"],
};

export const SETTINGS = "settings";
export const SETTINGS_REP = "repSettings";
export const SETTINGS_DE = "deSettings";
export const SETTINGS_MINDFLASH = "mindflashSettings";
export const ONBOARDING_REQUIREMENTS_KEY = "obRequirements";
export const ONBOARDING_REQUIREMENTS = [GROUP.REP, GROUP.STATUS_CHANGE, ONBOARDING_REQUIREMENTS_KEY];
export const ONBOARDING_SESSIONS = [GROUP.REP];
export const ONBOARDING_POSSIBLE_STATES = [
	GROUP.REP,
	GROUP.STATUS_CHANGE,
	ONBOARDING_REQUIREMENTS_KEY,
	"PossibleStates",
];
export const ONBOARDING_SEGMENTS = [
	GROUP.REP,
	GROUP.STATUS_CHANGE,
	ONBOARDING_REQUIREMENTS_KEY,
	"RepresentativeSegments",
];
export const REP_DISABLED_FEATURES = [GROUP.REP, "DisabledFeatures"];

export const REP_DETAILS = [GROUP.REP, GROUP.STATUS_CHANGE, "RepDetails", ONBOARDING_REQUIREMENTS_KEY];
export const REP_STATUS_HISTORIES = [GROUP.REP, GROUP.STATUS_CHANGE, "RepStatusHistories", ONBOARDING_REQUIREMENTS_KEY];
export const REP_ENROLLMENTS = [GROUP.REP, "RepEnrollments"];
export const REP_STATUS_BUTTONS = [GROUP.REP, GROUP.STATUS_CHANGE, "StatusButtons", ONBOARDING_REQUIREMENTS_KEY];
export const REP_SEGMENT_STATUS_BUTTONS = [
	GROUP.REP,
	GROUP.STATUS_CHANGE,
	ONBOARDING_REQUIREMENTS_KEY,
	"SegmentStatusButtons",
];
export const REP_RELATED_REPS = [GROUP.REP, "RelatedReps"];
export const REP_POT_DUPLICATES = [GROUP.REP, "DuplicateReps"];
export const REP_UPDATE_DATA = [GROUP.REP, "UpdateData"];
export const REP_ATTRIBUTES = [GROUP.REP, "RepAttributes"];

export const MANAGERS = "managers";
export const LANGUAGES = "languages";
export const REPTYPES = "repTypes";
export const TRAINERS = "trainers";

export const BACKGROUND_CHECKS = ["bgcs"];
export const BACKGROUND_CHECK_SCREENING_PROVIDERS = ["bgcScreeningProvider"];
export const DRUG_TESTS = ["DrugTests"];
export const DOCUMENTATION = [GROUP.REP, "Documentation"];
export const REP_DISCIPLINES = [GROUP.REP, "RepDisciplines"];
export const REP_DISCIPLINES_HISTORY = [GROUP.REP, "RepDisciplineHistories"];
export const REP_DISCIPLINES_TRAININGS = [GROUP.REP, "RepDisciplineTrainings"];

export const MIND_FLASH = ["MindFlash"];
export const MIND_FLASH_CUSTOM_FIELDS = [GROUP.REP, "MindFlashCustomFields"];
export const MIND_FLASH_ALL_TRAININGS = ["MindFlashAllTrainings"];
export const STATE_SPECIFIC_TRAININGS = [GROUP.REP, "StateSpecificTrainings"];
export const TRAINER_CERTIFICATIONS = [GROUP.REP, "TrainerCertifications"];

export const COMPLAINTS = ["Complaints"];
export const COMPLAINT = {
	CUSTOMER_ACTIONS: ["ComplaintCustomerActions"],
	CUSTOMER_ACTION_CATEGORIES: ["ComplaintCustomerActionCategories"],
	TYPES: ["ComplaintTypes"],
	LEVELS: ["ComplaintLevels"],
	SOURCES: ["ComplaintSources"],
	ENROLLMENT_SOURCES: ["ComplaintEnrollmentSources"],
	STAFFING_PARTNERS: ["ComplaintStaffingPartners"],
	ACTIONS: ["ComplaintActions"],
	FYI_REASONS: ["ComplaintFYIReasons"],
	USERS: ["ComplaintUsers"],
	REPORTING_TAGS: ["ReportingTags"],
	ROOT_CAUSES: ["RootCauses"],
	LOCATION_PARTNERS: ["LocationPartners"],
	AFFILIATE_PARTNERS: ["ComplaintAffiliatePartners"],
	BRANDS: ["ComplaintBrands"],
	TELSALES_BRANDS: ["ComplaintTelesalesBrands"],
	ENROLLMENTS: ["ComplaintEnrollments"],
	REPS: ["ComplaintReps"],
	MANAGERS: ["ComplaintManagers"],
	UTILITIES: ["ComplaintUtilities"],
};

export const REP_INCIDENT = {
	HISTORY: ["RepIncidentHistory"],
	SURVEY_RESPONSES: ["RepIncidentSurveyResponses"],
};

export const ENROLLMENTVALIDATIONS = ["EnrollmentValidations"];

export const IDENTIFICATION_REQUESTS = ["IdentificationRequests"];
export const REP_FIELD_AUDITS = ["RepFieldAudits"];
export const REP_FIELD_AUDIT_QUESTIONS = ["RepFieldAuditQuestions"];
export const REP_FIELD_AUDIT_FORM_TYPES = ["RepFieldAuditFormTypes"];
export const REP_FIELD_AUDIT_QUESTION_OPTIONS = ["RepFieldAuditQuestionOptions"];
export const REP_FIELD_AUDIT_COMPLAINT_TYPES = ["RepFieldAuditComplaintTypes"];
export const REP_FIELD_AUDIT_LOCATIONS = ["RepFieldAuditLocations"];

export const REP_PORTAL_META_SEARCH = ["RepPortalMetaSearch"];
export const REP_MACHINES_ACTIVE = ["RepActiveMachines"];
export const REP_MACHINES_ASSIGNED = ["RepAssignedMachines"];
export const REP_FEATURES = ["RepFeatures"];
export const AGENT_PORTAL_TAB_COUNTS = ["AgentPortalTabCounts"];

export const ONBOARDING_AUDITS = ["OnboardingAudits"];
export const REP_SURVEYS = ["RepSurveys"];
export const REP_SURVEY_RESULTS = ["RepSurveyResults"];

export const UPDATE_REP_DATA = "UpdateRepData";

export function useFilterItemQuery<T extends FilterItem = FilterItem>(
	name: string[],
	api: (...params) => Promise<(T | FilterItem)[]>,
	...params
) {
	const query = useQuery({ queryKey: [...name, ...params], queryFn: callApi });
	const [data, setData] = useState(query.data ?? emptyArray);
	const [loaded, setLoaded] = useState(query.isFetched);

	useEffect(() => {
		if (query.isFetched) setData(query.data);
		if (query.isFetched) setLoaded(true);
	}, [query.isFetched, query.data]);
	return [data, loaded] as [KeyedArray<T>, boolean];

	async function callApi() {
		return await api(...params).then(mapToKeyedArray);
	}
}

export function useFilterItemPascalQuery<T extends FilterItemPascal = FilterItemPascal>(
	name: string[],
	api: (...params) => Promise<(T | FilterItemPascal)[]>,
	...params
) {
	const query = useQuery({ queryKey: [...name, ...params], queryFn: callApi });
	const [data, setData] = useState(query.data ?? emptyArray);
	const [loaded, setLoaded] = useState(query.isFetched);

	useEffect(() => {
		if (query.isFetched) setData(query.data);
		if (query.isFetched) setLoaded(true);
	}, [query.isFetched, query.data]);
	return [data, loaded] as [KeyedArray<T>, boolean];

	async function callApi() {
		return await api(...params).then(mapToKeyedArrayPascal);
	}
}

export function getItems(allItems: KeyedArray<FilterItemPascal>, loaded: boolean, forceInclude?: FilterItemPascal) {
	if (!allItems) return [] as keyPair[];
	if (forceInclude) allItems.set(forceInclude);
	return allItems.map((c) => {
		return { id: c.Id, value: c.Name } as keyPair;
	});
}

const emptyArray = new KeyedArray<FilterItemPascal>([], (k) => k.Id);
